import React from 'react'
import { UserVehicle, VehicleInformationSocketModel } from '../../models'
import { useVehicleError, useVehicleImage, useVehicleInformation } from '../../hooks'
import VehicleTile from './VehicleTile'
import { useFeatureFlagsContext } from 'src/Libs/feature-flags/FeatureFlagsContext'
import { ManufacturerState } from '../../types'
import { useSearchParams } from 'react-router-dom'
import { MappedWallboxForWidgets } from 'src/Libs/dashboard-widgets/hooks/use-data-for-wallbox-widgets'
import VehicleDetailContainerModal from '../vehicle-details/VehicleDetailContainerModal'
import { MAX_CHARGE_LIMIT_VALUE } from '../../consts'

export const VEHICLE_ID_URL_PARAM = 'vehicleId'

interface Props {
  vehicle: UserVehicle
  installationId?: string
  connectedVehicle?: VehicleInformationSocketModel
  connectedWallbox?: MappedWallboxForWidgets
}

/**
 * This component fetches all data needed for the VehicleTile component.
 */
const VehicleTileContainer: React.FC<Props> = ({ vehicle, installationId, connectedVehicle, connectedWallbox }) => {
  const {
    id: vehicleId,
    name: vehicleName,
    vendorState,
    disconnectedAt,
    vin,
    customName,
    hasCustomImage,
    targetStateOfCharge,
    activeSchedule,
  } = vehicle
  const { imageUrl } = useVehicleImage(installationId, vehicleId.toString())
  const { vehicleInformation, hasError: vehicleInformationHasError } = useVehicleInformation(Number(installationId), vehicleId)
  const { vehicleError: userActionRequiredError } = useVehicleError(Number(installationId), vehicleId, Boolean(vehicleInformation?.hasError))

  const [searchParams, setSearchParams] = useSearchParams()
  const vehicleIdFromUrl = searchParams.get(VEHICLE_ID_URL_PARAM)
  const shouldOpenModal = Number(vehicleIdFromUrl) === vehicleId

  const { featureFlags, isFeatureFlagsLoading } = useFeatureFlagsContext()
  const newVendorAvailable = !isFeatureFlagsLoading && featureFlags?.isShowNewVehicleVendorsEnabled
  const vehicleAvailable = vendorState === ManufacturerState.available || (vendorState === ManufacturerState.unchecked && newVendorAvailable)

  const handleClose = (): void => {
    const newParams = new URLSearchParams(searchParams)
    newParams.delete(VEHICLE_ID_URL_PARAM)
    setSearchParams(newParams)
  }

  const handleVehicleTileClick = (): void => {
    if (!vehicleInformation) return
    const newParams = new URLSearchParams(searchParams)
    newParams.set(VEHICLE_ID_URL_PARAM, `${vehicleId}`)
    setSearchParams(newParams)
  }

  const commonParams = {
    installationId,
    vehicleName: customName || vehicleName,
    vehicleId,
    imageUrl,
    hasCustomImage,
    vin,
    vehicleAvailable,
    vendorState,
    onClose: handleClose,
    userActionRequiredError: userActionRequiredError,
  }

  const params = connectedVehicle
    ? {
        ...commonParams,
        /* use data from websocket when the vehicle is connected to wallbox */

        estimatedRange: connectedVehicle.estimatedRange,
        currentSoc: connectedVehicle.stateOfCharge,
        targetStateOfCharge: connectedVehicle.activeSchedule?.targetStateOfCharge ?? connectedVehicle.targetStateOfCharge,
        status: connectedVehicle.chargingState,
        sunmodeActive: Boolean(connectedWallbox?.sunMode),
        wallboxIndex: connectedWallbox?.number,
        wallboxChargingStatus: connectedWallbox?.chargingData?.chargingStatus,
        // Doesn't make sense to show 100% charge limit when maximum is 100%
        chargeLimitMax: connectedVehicle.chargeLimitMax === MAX_CHARGE_LIMIT_VALUE ? undefined : connectedVehicle.chargeLimitMax,
      }
    : {
        ...commonParams,
        estimatedRange: vehicleInformation?.estimatedRange,
        currentSoc: vehicleInformation?.stateOfCharge,
        targetStateOfCharge: activeSchedule?.targetStateOfCharge ?? targetStateOfCharge,
        status: vehicleInformation?.chargingState,
        /* currently hardcoded as long as we can't handle the sunmode state */
        sunmodeActive: true,
        // Doesn't make sense to show 100% charge limit when maximum is 100%
        chargeLimitMax: vehicleInformation?.chargeLimitMax === MAX_CHARGE_LIMIT_VALUE ? undefined : vehicleInformation?.chargeLimitMax,
      }

  return (
    <>
      <VehicleDetailContainerModal open={shouldOpenModal} {...params} />
      <VehicleTile
        onVehicleTileClick={handleVehicleTileClick}
        vehicleName={customName || vehicleName}
        vehicleId={vehicleId}
        installationId={installationId!}
        imageUrl={imageUrl}
        vehicleInformationHasError={vehicleInformationHasError}
        chargingState={vehicleInformation?.chargingState}
        estimatedRange={vehicleInformation?.estimatedRange}
        stateOfCharge={vehicleInformation?.stateOfCharge}
        isOnline={vehicleInformation?.isOnline ?? false}
        vendorState={vendorState}
        vehicleAvailable={vehicleAvailable}
        isVehicleConnectionInProgress={!vehicleInformation && !disconnectedAt}
        isVehicleDisconnected={!!disconnectedAt}
        vin={vin}
        userActionRequiredError={userActionRequiredError}
      />
    </>
  )
}

export default VehicleTileContainer
